import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  Check, 
  CheckCircle2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Clock from '../../../../../components/forms/Clock';
import LocationAutocompleteInput from '../../../../../components/forms/LocationAutocompleteInput';
import vouchersData from '../../../../../data/vouchers/vouchersData.json';

const defaultLabels = vouchersData?.contactLabels || [
  { label: 'Tour Manager', value: 'Tour Manager' },
  { label: 'Driver', value: 'Driver' },
  { label: 'Helper', value: 'Helper' },
  { label: 'Guide', value: 'Guide' },
  { label: 'Custom', value: 'Custom' }
];

// Helper to format date cleanly as "Wed, 1 Jul"
const formatDayDate = (dateStr, startDate, dayIdx) => {
  let d = null;
  if (dateStr) {
    const cleaned = String(dateStr).replace(/(\d+)(st|nd|rd|th)/g, "$1");
    const parsed = new Date(cleaned);
    if (!isNaN(parsed.getTime())) d = parsed;
  }
  if (!d && startDate) {
    const start = new Date(startDate);
    if (!isNaN(start.getTime())) {
      d = new Date(start);
      d.setDate(d.getDate() + dayIdx);
    }
  }
  if (!d) return dateStr || '';

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
};

const ItineraryContactModal = ({
  isOpen,
  onClose,
  mode = 'general', // 'general' | 'driver'
  dayNumber = null,
  dayEvents = [],
  itinerary = [],
  tourStartDate = null,
  contacts = [],
  assignedContacts = [],
  onSaveContact,
  onDeleteContact,
  initialContact = null
}) => {
  const [editingContactId, setEditingContactId] = useState(null);
  // Default general contact type is Tour Manager; Driver mode is locked to Driver
  const [newContactLabel, setNewContactLabel] = useState(mode === 'driver' ? 'Driver' : 'Tour Manager');
  const [customLabel, setCustomLabel] = useState('');
  const [newContactName, setNewContactName] = useState('');
  const [newContactNumber, setNewContactNumber] = useState('');
  
  // Selected Itinerary Day for Driver assignment
  const [selectedDayNumber, setSelectedDayNumber] = useState(dayNumber || 1);

  // Assignment State: 'whole_trip' (default) | 'whole_day' | 'event'
  const [assignmentType, setAssignmentType] = useState('whole_trip');
  const [selectedEventId, setSelectedEventId] = useState('');

  // Driver Specific Fields
  const [newContactVehicleType, setNewContactVehicleType] = useState('');
  const [newContactVehicleNumber, setNewContactVehicleNumber] = useState('');
  const [pickupLocation, setPickupLocation] = useState(null);
  const [dropLocation, setDropLocation] = useState(null);
  const [pickupTime, setPickupTime] = useState('');
  const [dropTime, setDropTime] = useState('');

  // Guide Specific Fields
  const [meetingPoint, setMeetingPoint] = useState(null);

  // Success Feedback Inside Modal
  const [successMessage, setSuccessMessage] = useState(null);

  // Build the list of itinerary days from the itinerary prop, falling back to dayNumber or Day 1
  const itineraryDays = useMemo(() => {
    if (Array.isArray(itinerary) && itinerary.length > 0) {
      return itinerary.map((day, idx) => ({
        day_number: day.day_number || (idx + 1),
        date: day.date,
        events: day.events || []
      }));
    }
    if (dayNumber) {
      return [{ day_number: Number(dayNumber), date: null, events: dayEvents || [] }];
    }
    return [{ day_number: 1, date: null, events: dayEvents || [] }];
  }, [itinerary, dayNumber, dayEvents]);

  // Current day events based on selectedDayNumber
  const currentDayEvents = useMemo(() => {
    const foundDay = itineraryDays.find(d => Number(d.day_number) === Number(selectedDayNumber));
    if (foundDay && foundDay.events && foundDay.events.length > 0) {
      return foundDay.events;
    }
    if (Number(selectedDayNumber) === Number(dayNumber) && dayEvents.length > 0) {
      return dayEvents;
    }
    return [];
  }, [itineraryDays, selectedDayNumber, dayNumber, dayEvents]);

  const resetForm = () => {
    setEditingContactId(null);
    setNewContactName('');
    setNewContactNumber('');
    setNewContactVehicleType('');
    setNewContactVehicleNumber('');
    setPickupLocation(null);
    setDropLocation(null);
    setPickupTime('');
    setDropTime('');
    setMeetingPoint(null);
    setCustomLabel('');
    // Default contact type is Tour Manager for general, Driver for driver mode
    setNewContactLabel(mode === 'driver' ? 'Driver' : 'Tour Manager');

    const defaultDay = dayNumber ? Number(dayNumber) : (itineraryDays[0]?.day_number || 1);
    setSelectedDayNumber(defaultDay);

    // Default assignment concept is Whole Trip
    setAssignmentType('whole_trip');
    const targetDayEvts = itineraryDays.find(d => Number(d.day_number) === Number(defaultDay))?.events || dayEvents;
    if (targetDayEvts && targetDayEvts.length > 0) {
      setSelectedEventId(targetDayEvts[0]?.id || `evt_${defaultDay}_1`);
    } else {
      setSelectedEventId('');
    }
  };

  const loadContactIntoForm = (c) => {
    if (!c) {
      resetForm();
      return;
    }
    setEditingContactId(c.id || c._id);
    setSuccessMessage(null);

    const standardRoles = ['Tour Manager', 'Driver', 'Helper', 'Guide'];
    const lbl = c.label || c.role || (mode === 'driver' ? 'Driver' : 'Tour Manager');

    if (mode === 'driver') {
      setNewContactLabel('Driver');
      setCustomLabel('');
    } else if (standardRoles.includes(lbl)) {
      setNewContactLabel(lbl);
      setCustomLabel('');
    } else {
      setNewContactLabel('Custom');
      setCustomLabel(lbl === 'Other' || lbl === 'Custom' ? '' : lbl);
    }

    setNewContactName(c.name || '');
    setNewContactNumber(c.number || '');

    const targetDay = c.day_number ? Number(c.day_number) : (dayNumber ? Number(dayNumber) : (itineraryDays[0]?.day_number || 1));
    setSelectedDayNumber(targetDay);

    const foundDayEvents = itineraryDays.find(d => Number(d.day_number) === Number(targetDay))?.events || dayEvents;
    if (c.assignment_type === 'event' || c.event_id) {
      setAssignmentType('event');
    } else if (c.assignment_type === 'whole_day' || (c.day_number && c.assignment_type !== 'whole_trip' && c.assignment_type !== 'all_days')) {
      setAssignmentType('whole_day');
    } else {
      setAssignmentType('whole_trip');
    }
    setSelectedEventId(c.event_id || (foundDayEvents[0]?.id || ''));

    setNewContactVehicleType(c.vehicle_type || '');
    setNewContactVehicleNumber(c.vehicle_number || '');
    setPickupLocation(c.pickup_location || null);
    setDropLocation(c.drop_location || null);
    setPickupTime(c.pickup_time || '');
    setDropTime(c.drop_time || '');
    setMeetingPoint(c.meeting_point || null);
  };

  useEffect(() => {
    if (isOpen) {
      if (initialContact) {
        loadContactIntoForm(initialContact);
      } else {
        resetForm();
        if (mode === 'driver') {
          setNewContactLabel('Driver');
        } else {
          setNewContactLabel('Tour Manager');
        }
      }
      setSuccessMessage(null);
    }
  }, [isOpen, initialContact, dayNumber, mode]);

  if (!isOpen) return null;

  const isDriver = mode === 'driver' || newContactLabel === 'Driver';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newContactName.trim()) {
      toast.error('Contact name is required.');
      return;
    }

    const finalRole = mode === 'driver'
      ? 'Driver'
      : (newContactLabel === 'Custom' ? (customLabel.trim() || 'Custom') : newContactLabel);

    const selectedEvt = isDriver && assignmentType === 'event'
      ? currentDayEvents.find(ev => (ev.id || ev._id) === selectedEventId)
      : null;

    const selectedDayObj = itineraryDays.find(d => Number(d.day_number) === Number(selectedDayNumber));
    const selectedDayIdx = itineraryDays.findIndex(d => Number(d.day_number) === Number(selectedDayNumber));
    const formattedDayDateStr = selectedDayObj
      ? formatDayDate(selectedDayObj.date, tourStartDate, selectedDayIdx >= 0 ? selectedDayIdx : 0)
      : '';

    // Determine final assignment parameters
    let finalAssignmentType = 'whole_trip';
    let finalDayNumber = null;
    let finalEventId = null;
    let finalEventTitle = null;
    let finalDateStr = 'Whole Trip';

    if (isDriver) {
      if (assignmentType === 'event') {
        finalAssignmentType = 'event';
        finalDayNumber = Number(selectedDayNumber);
        finalEventId = selectedEventId || (currentDayEvents[0]?.id || null);
        finalEventTitle = selectedEvt?.title || selectedEvt?.activity || 'Event';
        finalDateStr = formattedDayDateStr ? `Day ${selectedDayNumber} · ${formattedDayDateStr}` : `Day ${selectedDayNumber}`;
      } else if (assignmentType === 'whole_day') {
        finalAssignmentType = 'whole_day';
        finalDayNumber = Number(selectedDayNumber);
        finalDateStr = formattedDayDateStr ? `Day ${selectedDayNumber} · ${formattedDayDateStr}` : `Day ${selectedDayNumber}`;
      } else {
        finalAssignmentType = 'whole_trip';
        finalDayNumber = null;
        finalDateStr = 'Whole Trip';
      }
    } else {
      finalAssignmentType = 'whole_trip';
      finalDayNumber = null;
      finalDateStr = 'Whole Trip';
    }

    const contactObj = {
      id: editingContactId || `cnt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      name: newContactName.trim(),
      number: newContactNumber.trim(),
      label: finalRole,
      role: finalRole,
      assignment_type: finalAssignmentType,
      day_number: finalDayNumber,
      event_id: finalEventId,
      event_title: finalEventTitle,
      vehicle_type: isDriver ? newContactVehicleType.trim() : '',
      vehicle_number: isDriver ? newContactVehicleNumber.trim() : '',
      pickup_location: isDriver ? pickupLocation : null,
      drop_location: (isDriver && assignmentType === 'event') ? dropLocation : null,
      pickup_time: (isDriver && assignmentType === 'event') ? pickupTime : '',
      drop_time: (isDriver && assignmentType === 'event') ? dropTime : '',
      meeting_point: newContactLabel === 'Guide' ? meetingPoint : null,
      date: finalDateStr
    };

    if (onSaveContact) {
      onSaveContact(contactObj, Boolean(editingContactId));
    }

    let msg = '';
    if (isDriver) {
      if (assignmentType === 'event') {
        msg = `Driver assigned to ${finalEventTitle || 'Event'} (Day ${selectedDayNumber}).`;
      } else if (assignmentType === 'whole_day') {
        msg = `Driver assigned to Day ${selectedDayNumber}.`;
      } else {
        msg = 'Driver assigned across Whole Trip.';
      }
    } else {
      msg = `${finalRole} assigned across Whole Trip.`;
    }
    toast.success(msg);
    resetForm();
    onClose();
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 md:p-6 bg-slate-900/50 backdrop-blur-xs animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-2xl shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3.5rem)] my-auto transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-white shrink-0">
          <div>
            {mode === 'driver' && (
              <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider block">
                {assignmentType === 'whole_trip' ? 'Whole Trip Driver' : `Day ${selectedDayNumber} Driver`}
              </span>
            )}
            <h3 className="text-sm sm:text-base font-semibold text-slate-800 tracking-tight leading-tight">
              {mode === 'driver' 
                ? (editingContactId ? 'Edit Driver' : 'Add Driver') 
                : (editingContactId ? 'Edit Contact' : 'Add Contact')}
            </h3>
            <p className="text-xs text-slate-500 font-normal mt-0.5">
              {mode === 'driver'
                ? 'Assign driver, vehicle and transfer details for this booking'
                : 'Add or edit contacts and service providers for this booking'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form onSubmit={handleSubmit} className="px-5 py-3.5 overflow-y-auto space-y-3.5 flex-1">
          {/* Edit Contacts Chip Selector (Visible in general mode when existing contacts are present) */}
          {mode === 'general' && contacts && contacts.length > 0 && (
            <div className="space-y-1.5 pb-2.5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  Edit Contacts
                </label>
                <span className="text-[10px] text-slate-400">
                  Select a contact to edit
                </span>
              </div>
              <div className="flex flex-wrap gap-2 p-1.5 bg-slate-50/80 rounded-xl border border-slate-200/80">
                {contacts.map((c, idx) => {
                  const cId = c.id || c._id || `c_${idx}`;
                  const isSelected = editingContactId === cId;
                  const displayRole = c.label || c.role || 'Contact';
                  return (
                    <button
                      key={cId}
                      type="button"
                      onClick={() => loadContactIntoForm(c)}
                      className={`px-3 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1.5 cursor-pointer ${
                        isSelected
                          ? 'bg-[#067978] text-white shadow-2xs font-medium'
                          : 'bg-white border border-slate-200/90 text-slate-600 hover:border-slate-300 font-normal'
                      }`}
                    >
                      <span>{c.name || 'Unnamed'}</span>
                      <span className={isSelected ? 'text-teal-100 text-[11px]' : 'text-slate-400 text-[11px]'}>
                        · {displayRole}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Success Banner */}
          {successMessage && (
            <div className="flex items-center justify-between gap-2 p-2 bg-emerald-50 border border-emerald-200/80 rounded-lg text-xs text-emerald-800 font-medium">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                <span>{successMessage}</span>
              </div>
              <button 
                type="button" 
                onClick={() => setSuccessMessage(null)}
                className="text-emerald-600 hover:text-emerald-800 text-xs px-1 cursor-pointer"
              >
                ✕
              </button>
            </div>
          )}

          {/* Primary Info Row: Contact Type (in general mode), Name, Phone */}
          {mode === 'general' ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Contact Type <span className="text-rose-500">*</span>
                </label>
                <select
                  value={
                    ['Tour Manager', 'Driver', 'Helper', 'Guide'].includes(newContactLabel)
                      ? newContactLabel
                      : 'Custom'
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewContactLabel(val);
                    if (val !== 'Driver') {
                      setNewContactVehicleType('');
                      setNewContactVehicleNumber('');
                      setPickupLocation(null);
                      setDropLocation(null);
                      setPickupTime('');
                      setDropTime('');
                    }
                    if (val !== 'Guide') {
                      setMeetingPoint(null);
                    }
                    if (val !== 'Custom') {
                      setCustomLabel('');
                    }
                  }}
                  className="w-full h-[36px] px-2.5 bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs cursor-pointer"
                >
                  {defaultLabels.map(lbl => (
                    <option key={lbl.value} value={lbl.value}>{lbl.label}</option>
                  ))}
                </select>
                {newContactLabel === 'Custom' && (
                  <input
                    type="text"
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    placeholder="Enter custom role..."
                    className="w-full mt-1.5 px-2.5 h-[34px] bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs"
                  />
                )}
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newContactName}
                  onChange={(e) => setNewContactName(e.target.value)}
                  placeholder="e.g. Ramesh Sawant"
                  className="w-full px-3 h-[36px] bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs"
                />
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={newContactNumber}
                  onChange={(e) => setNewContactNumber(e.target.value)}
                  placeholder="+91 XXXXX XXXXX"
                  className="w-full px-3 h-[36px] bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs font-mono"
                />
              </div>
            </div>
          ) : (
            /* Mode === 'driver' (Contact Type locked to Driver, not shown) */
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newContactName}
                  onChange={(e) => setNewContactName(e.target.value)}
                  placeholder="e.g. Ramesh Sawant"
                  className="w-full px-3 h-[36px] bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs"
                />
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={newContactNumber}
                  onChange={(e) => setNewContactNumber(e.target.value)}
                  placeholder="+91 XXXXX XXXXX"
                  className="w-full px-3 h-[36px] bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs font-mono"
                />
              </div>
            </div>
          )}

          {/* DRIVER SPECIFIC FIELDS */}
          {isDriver && (
            <div className="space-y-3 pt-1 border-t border-slate-100">
              {/* Vehicle Type & Number */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">
                    Vehicle Type / Name
                  </label>
                  <input
                    type="text"
                    value={newContactVehicleType}
                    onChange={(e) => setNewContactVehicleType(e.target.value)}
                    placeholder="e.g. Toyota Innova, SUV"
                    className="w-full px-3 h-[36px] bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">
                    Vehicle Number
                  </label>
                  <input
                    type="text"
                    value={newContactVehicleNumber}
                    onChange={(e) => setNewContactVehicleNumber(e.target.value)}
                    placeholder="e.g. GA-01-A-1234"
                    className="w-full px-3 h-[36px] bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs font-mono"
                  />
                </div>
              </div>

              {/* Pickup and Drop Locations */}
              {assignmentType !== 'event' ? (
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">
                    Pickup Location <span className="text-slate-400 font-normal">(Optional)</span>
                  </label>
                  <LocationAutocompleteInput
                    value={pickupLocation}
                    onChange={(loc) => setPickupLocation(loc)}
                    placeholder="Search pickup point..."
                    className="!h-[36px]"
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Pickup Location + Pickup Time */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Pickup Location <span className="text-slate-400 font-normal">(Optional)</span>
                      </label>
                      <LocationAutocompleteInput
                        value={pickupLocation}
                        onChange={(loc) => setPickupLocation(loc)}
                        placeholder="Search pickup point..."
                        className="!h-[36px]"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Pickup Time (24h)
                      </label>
                      <Clock
                        value={pickupTime}
                        onChange={(val) => setPickupTime(val)}
                        placeholder="00:00"
                        inputClassName="h-[36px] px-3 text-xs font-normal rounded-xl"
                      />
                    </div>
                  </div>

                  {/* Drop Location + Drop Time */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Drop Location <span className="text-slate-400 font-normal">(Optional)</span>
                      </label>
                      <LocationAutocompleteInput
                        value={dropLocation}
                        onChange={(loc) => setDropLocation(loc)}
                        placeholder="Search drop point..."
                        className="!h-[36px]"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Drop Time (24h)
                      </label>
                      <Clock
                        value={dropTime}
                        onChange={(val) => setDropTime(val)}
                        placeholder="00:00"
                        inputClassName="h-[36px] px-3 text-xs font-normal rounded-xl"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* GUIDE SPECIFIC FIELDS: Meeting Point */}
          {newContactLabel === 'Guide' && mode !== 'driver' && (
            <div className="pt-1 border-t border-slate-100">
              <label className="block text-[11px] font-medium text-slate-600 mb-1">
                Meeting Point <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <LocationAutocompleteInput
                value={meetingPoint}
                onChange={(loc) => setMeetingPoint(loc)}
                placeholder="Search meeting point..."
                className="!h-[36px]"
              />
            </div>
          )}

          {/* ASSIGN CONTACT SECTION */}
          {isDriver ? (
            <div className="pt-2.5 border-t border-slate-100 space-y-2.5">
              <div>
                <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider block">
                  ASSIGN CONTACT
                </span>
              </div>

              {/* 3 Scope Options: Whole Trip (default), Specific Day, Specific Event */}
              <div className="flex items-center gap-5 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-normal text-slate-700">
                  <input
                    type="radio"
                    name="assignmentType"
                    value="whole_trip"
                    checked={assignmentType === 'whole_trip'}
                    onChange={() => setAssignmentType('whole_trip')}
                    className="text-[#067978] focus:ring-[#067978] h-3.5 w-3.5"
                  />
                  <span>Whole Trip</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-normal text-slate-700">
                  <input
                    type="radio"
                    name="assignmentType"
                    value="whole_day"
                    checked={assignmentType === 'whole_day'}
                    onChange={() => {
                      setAssignmentType('whole_day');
                      if (!selectedDayNumber) {
                        setSelectedDayNumber(dayNumber || 1);
                      }
                    }}
                    className="text-[#067978] focus:ring-[#067978] h-3.5 w-3.5"
                  />
                  <span>Specific Day</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-normal text-slate-700">
                  <input
                    type="radio"
                    name="assignmentType"
                    value="event"
                    checked={assignmentType === 'event'}
                    onChange={() => {
                      setAssignmentType('event');
                      if (!selectedDayNumber) {
                        setSelectedDayNumber(dayNumber || 1);
                      }
                      if (!selectedEventId && currentDayEvents.length > 0) {
                        setSelectedEventId(currentDayEvents[0].id || `evt_${selectedDayNumber || dayNumber || 1}_1`);
                      }
                    }}
                    className="text-[#067978] focus:ring-[#067978] h-3.5 w-3.5"
                  />
                  <span>Specific Event</span>
                </label>
              </div>

              {/* Whole Trip Feedback */}
              {assignmentType === 'whole_trip' && (
                <div className="text-xs text-slate-600 font-normal bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg inline-block">
                  This contact will be added for the whole trip.
                </div>
              )}

              {/* Specific Day Selector */}
              {assignmentType === 'whole_day' && (
                <div className="space-y-1.5 pt-1">
                  <div className="text-xs text-slate-600 font-normal bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg inline-block mb-1">
                    This contact will be added for the selected day.
                  </div>
                  <label className="block text-[11px] font-medium text-slate-500">
                    Select Day <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {itineraryDays.map((day, idx) => {
                      const dNum = day.day_number || (idx + 1);
                      const dDate = formatDayDate(day.date, tourStartDate, idx);
                      const isSelected = Number(selectedDayNumber) === Number(dNum);
                      return (
                        <button
                          key={dNum}
                          type="button"
                          onClick={() => setSelectedDayNumber(dNum)}
                          className={`px-3 py-1.5 rounded-xl text-xs transition-all cursor-pointer font-medium ${
                            isSelected
                              ? 'bg-[#067978] text-white shadow-2xs'
                              : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          Day {dNum}{dDate ? ` · ${dDate}` : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Specific Event Selector (Day + Event) */}
              {assignmentType === 'event' && (
                <div className="space-y-2.5 pt-1">
                  <div className="text-xs text-slate-600 font-normal bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg inline-block">
                    This contact will be assigned to the selected event.
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-medium text-slate-500">
                      Select Day <span className="text-rose-500">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {itineraryDays.map((day, idx) => {
                        const dNum = day.day_number || (idx + 1);
                        const dDate = formatDayDate(day.date, tourStartDate, idx);
                        const isSelected = Number(selectedDayNumber) === Number(dNum);
                        return (
                          <button
                            key={dNum}
                            type="button"
                            onClick={() => {
                              setSelectedDayNumber(dNum);
                              const evts = day.events || [];
                              if (evts.length > 0) {
                                setSelectedEventId(evts[0].id || `evt_${dNum}_1`);
                              } else {
                                setSelectedEventId('');
                              }
                            }}
                            className={`px-3 py-1.5 rounded-xl text-xs transition-all cursor-pointer font-medium ${
                              isSelected
                                ? 'bg-[#067978] text-white shadow-2xs'
                                : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            Day {dNum}{dDate ? ` · ${dDate}` : ''}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-1">
                      Select Event <span className="text-rose-500">*</span>
                    </label>
                    {currentDayEvents.length > 0 ? (
                      <select
                        value={selectedEventId}
                        onChange={(e) => setSelectedEventId(e.target.value)}
                        className="w-full h-[36px] px-3 bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs cursor-pointer"
                      >
                        {currentDayEvents.map((evt, idx) => (
                          <option key={evt.id || idx} value={evt.id || `evt_${selectedDayNumber}_${idx + 1}`}>
                            {idx + 1}. {evt.title || evt.activity || `Event ${idx + 1}`}{evt.time ? ` (${evt.time})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-xs text-slate-400 py-1 font-normal">
                        No events found for Day {selectedDayNumber}. It will be saved as day contact.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* NON-DRIVER CONTACT SCOPE: Default Whole Trip */
            <div className="pt-2 border-t border-slate-100">
              <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider block">
                ASSIGN CONTACT
              </span>
              <div className="text-xs text-slate-600 font-normal bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg inline-block mt-1">
                This contact will be added for the whole trip.
              </div>
            </div>
          )}

          {/* Form Action Bar: New Contact vs Edit Contact */}
          <div className="pt-2.5 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
            {editingContactId ? (
              <>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                >
                  Cancel Edit
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-[#067978] hover:bg-[#056564] text-white text-xs font-medium rounded-lg shadow-xs transition-all active:scale-95 cursor-pointer flex items-center gap-1.5"
                >
                  <Check size={13} />
                  <span>Save Changes</span>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-[#067978] hover:bg-[#056564] text-white text-xs font-medium rounded-lg shadow-xs transition-all active:scale-95 cursor-pointer flex items-center gap-1.5"
                >
                  <Check size={13} />
                  <span>Add Contact</span>
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ItineraryContactModal;
